import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { conversationApiService } from "../services/conversationApiService";
import {
  fromConversationWithClientDtoToConversationWithClient,
  type ConversationWithClient,
} from "../types/conversationTypes";

const staleTime = 5 * 60 * 1000; // 5 minutes

// Query keys
export const conversationKeys = {
  all: ["conversation"] as const,
  list: (businessId?: string, phoneNumberId?: string, clientId?: string, userId?: string) =>
    [
      ...conversationKeys.all,
      "list",
      { businessId, phoneNumberId, clientId, userId },
    ] as const,
  detail: (id: string, businessId?: string, userId?: string) =>
    [...conversationKeys.all, "detail", { businessId, id, userId }] as const,
};

export const useConversations = (phoneNumberId?: string) => {
  const { businessId } = useCurrentBusiness();
  const { user } = useAuth();

  return useQuery<ConversationWithClient[], Error>({
    queryKey: conversationKeys.list(businessId, phoneNumberId, undefined, user?.id),
    queryFn: async () => {
      const dtos = await conversationApiService.getList({ businessId, phoneNumberId });
      return dtos.map(fromConversationWithClientDtoToConversationWithClient)
        .filter((conversation) => conversation.businessId === businessId && !conversation.deletedAt);
    },
    enabled: !!user && !!businessId,
    staleTime,
  });
};

export const useConversation = (id?: string) => {
  const { businessId } = useCurrentBusiness();
  const { user } = useAuth();
  return useQuery<ConversationWithClient, Error>({
    queryKey: conversationKeys.detail(id || "", businessId, user?.id),
    queryFn: async () => {
      const dto = await conversationApiService.getById(id!);
      const conversation = fromConversationWithClientDtoToConversationWithClient(dto);
      if (conversation.businessId !== businessId || conversation.deletedAt) {
        throw new Error("Conversation not found in this business.");
      }
      return conversation;
    },
    enabled: !!user && !!businessId && !!id,
    staleTime,
  });
};

export const useClientConversations = (
  clientId?: string,
  phoneNumberId?: string
) => {
  const { businessId } = useCurrentBusiness();
  const { user } = useAuth();
  return useQuery<ConversationWithClient[], Error>({
    queryKey: conversationKeys.list(businessId, phoneNumberId, clientId, user?.id),
    queryFn: async () => {
      try {
        const dtos = await conversationApiService.getList({
          businessId: businessId || undefined,
          phoneNumberId,
          clientId,
        });
        return dtos.map(fromConversationWithClientDtoToConversationWithClient);
      } catch (error) {
        // If no conversations found, return empty array
        if (error instanceof Error && error.message.includes("not found")) {
          return [];
        }
        throw error;
      }
    },
    enabled: !!user && !!businessId && !!phoneNumberId && !!clientId,
    staleTime,
  });
};
